import { ADMINS_FEEDBACK_RULES } from '../admins/adminsFeedback';
import { DELIVERIES_FEEDBACK_RULES } from '../deliveries/deliveriesFeedback';
import { ECOMMERCES_FEEDBACK_RULES } from '../ecommerces/ecommercesFeedback';
import { PICKUP_REQUESTS_FEEDBACK_RULES } from '../pickupRequests/pickupRequestsFeedback';
import { WEBHOOKS_FEEDBACK_RULES } from '../webhooks/webhooksFeedback';
import { PICKUP_POINTS_FEEDBACK_RULES } from '../pickupPoints/pickupPointsFeedback';
import { WAREHOUSE_EXPLICIT_NOTIFY_THUNKS, WAREHOUSE_READ_THUNKS } from './warehouseFeedback';
import { OPERATOR_EXPLICIT_NOTIFY_THUNKS, OPERATOR_READ_THUNKS } from './operatorFeedback';

/**
 * How every thunk tells the user what happened. Each `typePrefix` belongs to
 * exactly one of the lists below, and `feedbackCoverage.test.js` fails when a
 * thunk is in none: a new action cannot ship silent by accident.
 */

/**
 * Thunks whose toasts come from here, through `feedbackListener`.
 *
 * - `pending`: a progress toast while it runs, under the thunk's `requestId`,
 *   so `progressUpdated` with that key moves its bar. Only for triggers that
 *   vanish on click (a menu item), or browser work that takes a while.
 * - `success`: a string, `(action) => string | {message, tone, action,
 *   sticky}`, or `'response'` for the sentence the backend wrote
 *   (`readSuccessMessage`).
 * - `error`: `'toast'` (default), or `'none'` when the open surface shows it.
 * - `testId` / `errorTestId`: the toast's handle, when a test needs to find it.
 *
 * `arg.feedback === false` silences one call.
 */
export const FEEDBACK_RULES = {
  ...ADMINS_FEEDBACK_RULES,
  ...DELIVERIES_FEEDBACK_RULES,
  ...ECOMMERCES_FEEDBACK_RULES,
  ...PICKUP_REQUESTS_FEEDBACK_RULES,
  ...WEBHOOKS_FEEDBACK_RULES,
  ...PICKUP_POINTS_FEEDBACK_RULES,
};

/** Reads: they never toast. A failed load shows its `ErrorState` where the data goes. */
export const READ_THUNKS = [
  'admins/fetchStaffAdmins',
  'admins/fetchStaffAdminDetail',
  'admins/fetchAdminOptions',
  'attention/fetchAvailableCouriers',
  'attention/fetchAttention',
  'couriers/fetchAll',
  'couriers/fetchStaffAll',
  'couriers/fetchTemplates',
  'dashboard/fetchDashboard',
  'deliveries/getDeliveries',
  'deliveries/fetchDeliveryFilterOptions',
  // Its failure shows inside the «Cambiar courier» dialog, with a retry.
  'deliveries/fetchCourierOptions',
  'deliveryItems/fetchDeliveryItems',
  'ecommerces/fetchIntegrations',
  'ecommerces/fetchWarehouses',
  'externalIntegrations/fetchAll',
  'externalIntegrations/fetchSellers',
  'externalIntegrations/fetchStaffAll',
  'geo/fetchRegions',
  'orderDetails/fetchOrderDetails',
  'orderDetails/fetchAvailableCouriers',
  'orderDetails/fetchOrderWarehouses',
  'orderDetails/fetchDrivers',
  'partnerships/fetchAvailable',
  'partnerships/fetchAll',
  'partnerships/fetchStaffAll',
  'pickupRequests/fetchCouriers',
  'pickupRequests/fetchPickupRequests',
  'pickupRequests/fetchEligibleOrders',
  'pickupRequests/fetchWarehouses',
  'pickups/fetchPickups',
  'pickupPoints/fetchPickupPoints',
  'sellerDetails/fetchCompanyDetails',
  'sellerDetails/fetchSellerCouriers',
  'sellerDetails/fetchAvailableCouriers',
  'sellers/fetchSellers',
  'sellers/fetchSellerOptions',
  'sellers/fetchSellerWarehouseOptions',
  'sellers/fetchCompanyWarehouseOptions',
  'sellers/fetchOwnWarehouseOptions',
  'sellers/checkSellerEmail',
  'tracking/searchOrders',
  'tracking/searchPartnerOrders',
  'tracking/fetchTrackedOrder',
  'upselling/fetchIncoming',
  'upselling/fetchPendingCount',
  'user/fetchCurrentUser',
  'webhooks/fetchWebhooks',
  'zones/fetchZones',
  'zones/fetchZoneDetail',
  'zones/fetchZoneCommunes',
  'zones/fetchZoneAssignments',
  'zones/fetchZoneFormOptions',
  'zones/fetchAdminZoneOptions',
  'zones/fetchWarehouseOptions',
  // The warehouse module's reads live with its writes, in `warehouseFeedback`.
  ...WAREHOUSE_READ_THUNKS,
  // The warehouse operator's, in `operatorFeedback`.
  ...OPERATOR_READ_THUNKS,
];

/**
 * Mutations whose screen already reports both outcomes on its own: a result
 * panel, a page toast, the Activity Center, a navigation.
 */
export const EXPLICIT_NOTIFY_THUNKS = [
  'attention/requestTrackingNumbers',
  'attention/changeCourierBulk',
  'auth/loginUser',
  'auth/logoutUser',
  'deliveries/dispatchDeliveriesToCourier',
  // Its dialog ends on a tally of what was queued, skipped and to cancel by hand.
  'deliveries/changeDeliveriesCourier',
  // Same shape: its dialog ends on what changed and what was skipped.
  'deliveries/changeDeliveriesPickupPoint',
  'deliveries/exportDeliveries',
  'deliveries/exportStatusHistory',
  // It leaves for Shopify; a refusal shows in the warehouse dialog that asked.
  'ecommerces/reauthorizeShopify',
  'sellers/createSeller',
  'sellers/requestDownloadSellers',
  'upselling/manageRequests',
  'upselling/assign',
  'user/requestSecureCode',
  'user/verifySecureCode',
  'user/changePassword',
  ...WAREHOUSE_EXPLICIT_NOTIFY_THUNKS,
  ...OPERATOR_EXPLICIT_NOTIFY_THUNKS,
];

/**
 * Mutations that still leave the user without knowing what happened — a silent
 * success, an error nobody sees — and the wave of the plan that fixes each.
 * This list only shrinks: moving an entry out of it is how a wave is done.
 */
export const NOT_YET_COVERED = {
  'couriers/create': 'ola-1',
  'couriers/update': 'ola-1',
  'couriers/delete': 'ola-1',
  'deliveryItems/fetchAllDeliveryItemLabels': 'ola-2',
  'deliveryItems/createDeliveryItem': 'ola-1',
  'deliveryItems/deleteDeliveryItem': 'ola-1',
  'deliveryItems/setDeliveryItemsCount': 'ola-1',
  'deliveryItems/updateDeliveryItemLogistics': 'ola-1',
  'deliveryItems/updateDeliveryItemStatus': 'ola-1',
  'ecommerces/initMeliAuth': 'ola-1',
  'ecommerces/initShopifyAuth': 'ola-5',
  'ecommerces/retryMeliAuth': 'ola-1',
  'externalIntegrations/create': 'ola-1',
  'externalIntegrations/update': 'ola-1',
  'externalIntegrations/delete': 'ola-1',
  'orderDetails/updateOrderShipping': 'ola-1',
  'orderDetails/updateOrderRecipient': 'ola-1',
  'orderDetails/updateOrderState': 'ola-1',
  'orderDetails/updateStatusHistoryDetail': 'ola-1',
  'orderDetails/fulfillOrder': 'ola-2',
  'orderDetails/dispatchPreparedOrder': 'ola-2',
  'orderDetails/changeOrderCourier': 'ola-2',
  'orderDetails/prepareOrderLineItem': 'ola-2',
  'orderDetails/uploadProofOfDelivery': 'ola-5',
  'orderDetails/deleteProofOfDelivery': 'ola-5',
  'partnerships/create': 'ola-1',
  'partnerships/delete': 'ola-1',
  'pickups/reprintPickupLabel': 'ola-2',
  'sellerDetails/changeSellerPassword': 'ola-1',
  'sellerDetails/requestDownloadWarehouses': 'ola-1',
  'sellerDetails/createSellerCourier': 'ola-1',
  'sellerDetails/updateSellerCourier': 'ola-1',
  'sellerDetails/updateWarehousePickupSchedule': 'ola-1',
  'sellerDetails/deleteSellerCourier': 'ola-1',
  'user/updateProfile': 'ola-1',
  'user/updateOperativeMails': 'ola-2',
  'zones/createZone': 'ola-1',
  'zones/updateZone': 'ola-1',
  'zones/deleteZone': 'ola-1',
  'zones/createAssignment': 'ola-1',
  'zones/updateAssignment': 'ola-1',
  'zones/deleteAssignment': 'ola-1',
};
